import React from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { FieldValues, Control, Path, useController } from 'react-hook-form';

import InputLabelText from '../InputLabelText/InputLabelText';
import { getErrorMessage } from '../Input/ValidationErrors';

type PatternType = 'text' | 'date' | 'url' | 'number' | 'password' | 'email';

export const onlyLowercase = (value: string) => {
  const trim = value.trim();
  if (trim) {
    return /^[a-z\s]+$/.test(value) || 'Only lowercase letters are permitted';
  }
  return true;
};
export const onlyNumbers = (value: string) => {
  const trim = value.trim();
  if (trim) {
    return /^[0-9]*$/.test(value) || 'Must be an integer';
  }
  return true;
};
export const checkRange = (value: string) => {
  const trim = value.trim();
  if (trim) {
    return (parseInt(value) > 0 && parseInt(value) < 100) || 'Value exceeds the max limit';
  }
  return true;
};

export const alphanumeric =
  (msg = 'Use only spaces and alphanumeric characters.') =>
  (value: string) =>
    /^[a-zA-Z0-9\s]*$/.test(value) || msg;

interface ValidateObject {
  [key: string]: (value: string) => boolean | string | Promise<boolean | string>;
}

export interface TextInputProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: string | null;
  placeholder?: string | null;
  required?: boolean;
  minLength?: number | null;
  maxLength?: number | null;
  type?: PatternType;
  disabled?: boolean;
  hideLabel?: boolean;
  rows?: number;
  prefix?: string;
  endAdornment?: React.ReactNode;
  showModified?: boolean;
  showOptionalLabel?: boolean;
  min?: number;
  max?: number;
  minExclusive?: boolean;
  maxExclusive?: boolean;
  validate?: ValidateObject;
  helperText?: React.ReactNode;
  changeRequested?: boolean;
  floatingElement?: React.ReactNode;
  readOnly?: boolean;
  hideErrorMsg?: boolean;
  hideHelperTextOnError?: boolean;
  autocomplete?: HTMLInputElement['autocomplete'];
  customErrorMessages?: {
    email?: string;
    maxLength?: string;
    minLength?: string;
    min?: string;
    max?: string;
    notMinExclusive?: string;
    notMaxExclusive?: string;
  };
  ignoreFs?: boolean;
  hideMaxLengthAdornment?: boolean;
  /** When true, validation errors do not show the TextField error outline (border/focus ring). */
  hideValidationOutline?: boolean;
  /**
   * When `rows` > 1 (multiline), sets CSS `resize` on the textarea so the user can drag to change height/width.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/resize
   */
  multilineResize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /**
   * When true with `type="text"`, skips the default pattern that rejects all-whitespace values.
   * Use when the field may be empty while related form state (e.g. chips) satisfies the real requirement.
   */
  skipBlankTextPattern?: boolean;
  /** Passed to the native input/textarea (e.g. onKeyDown, onPaste, aria-label). */
  htmlInputSlotProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement> & React.InputHTMLAttributes<HTMLInputElement>;
}

const getPattern = (type: PatternType) => {
  switch (type) {
    case 'text':
      return /(.|\s)*\S(.|\s)*/;
    case 'number':
      return /^-?\d+\.?\d*$/;
    case 'email':
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    default:
      return undefined;
  }
};

const TextInput = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  required = false,
  minLength,
  maxLength = 500,
  type = 'text',
  disabled = false,
  hideLabel = false,
  rows = 1,
  prefix,
  endAdornment,
  showModified = false,
  showOptionalLabel,
  min,
  max,
  minExclusive,
  maxExclusive,
  validate,
  helperText,
  changeRequested = false,
  floatingElement,
  readOnly = false,
  hideErrorMsg = false,
  hideHelperTextOnError = false,
  autocomplete = '',
  customErrorMessages = {},
  ignoreFs = false,
  hideMaxLengthAdornment = false,
  hideValidationOutline = false,
  multilineResize,
  htmlInputSlotProps,
  skipBlankTextPattern = false,
}: TextInputProps<T>) => {
  const {
    field,
    fieldState: { error, isDirty },
  } = useController({
    control,
    name,
    rules: {
      required,
      minLength: minLength || undefined,
      maxLength: maxLength || undefined,
      pattern: skipBlankTextPattern && type === 'text' ? undefined : getPattern(type),
      min: type === 'number' && !minExclusive ? min : undefined,
      max: type === 'number' && !maxExclusive ? max : undefined,
      validate: {
        notMinExclusive: (v) => (type === 'number' && min !== undefined && minExclusive ? parseFloat(v) > min : true),
        notMaxExclusive: (v) => (type === 'number' && max !== undefined && maxExclusive ? parseFloat(v) < max : true),
        ...validate,
      },
    },
  });

  const errorMessage = getErrorMessage(error, { type, minLength, maxLength, min, max }, customErrorMessages);

  const isModified = showModified && isDirty;
  const changeRequestedStyles = changeRequested
    ? {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: (theme: Theme) => theme.palette.changeRequested.main,
          },
          '&:hover fieldset': {
            borderColor: (theme: Theme) => theme.palette.changeRequested.main,
          },
        },
      }
    : {};

  const adornment =
    maxLength && !hideMaxLengthAdornment ? (
      <InputAdornment
        position="end"
        aria-hidden={true}
        sx={{ width: '100%', textAlign: 'right', paddingTop: (theme) => theme.spacing(1.25) }}
      >
        <Typography color="textSecondary" sx={{ width: '100%' }}>
          <Box
            component="span"
            sx={{ color: (theme) => (field.value?.length > maxLength ? theme.palette.error.main : 'inherit') }}
          >
            {field.value?.length}
          </Box>
          /{maxLength}
        </Typography>
      </InputAdornment>
    ) : null;

  const labelErrorMessage = hideValidationOutline ? undefined : errorMessage;

  const multilineResizeSx =
    rows > 1 && multilineResize && multilineResize !== 'none'
      ? {
          '& textarea': {
            resize: multilineResize,
            boxSizing: 'border-box' as const,
          },
        }
      : {};

  return (
    <InputLabelText
      label={label}
      name={name}
      required={showOptionalLabel !== undefined ? !showOptionalLabel : required}
      errorMessage={labelErrorMessage}
      hideLabel={hideLabel}
      prefix={prefix}
      showModified={isModified}
      helperText={hideHelperTextOnError ? (error ? '' : helperText) : helperText}
      disabled={disabled}
      changeRequested={changeRequested}
      floatingElement={floatingElement}
      hideErrorMsg={hideValidationOutline || hideErrorMsg}
    >
      <TextField
        {...field}
        value={field.value ?? ''}
        id={`${name}-input`}
        sx={{
          background: (theme) => theme.palette.background.paper,
          ...changeRequestedStyles,
          ...multilineResizeSx,
          input: {
            '&::placeholder': {
              opacity: 1,
              color: 'text.secondary',
            },
          },
          '& textarea::placeholder': {
            color: 'text.secondary',
            opacity: 1,
          },
        }}
        error={!hideValidationOutline && !!error}
        variant="outlined"
        type={type !== 'number' && type !== 'email' ? type : 'text'}
        placeholder={placeholder || ''}
        disabled={disabled}
        rows={rows}
        multiline={rows > 1}
        fullWidth={true}
        autoComplete={autocomplete}
        slotProps={{
          input: {
            endAdornment: rows > 1 ? adornment : endAdornment,
            readOnly,
            sx: { flexDirection: rows > 1 ? 'column' : undefined },
          },
          ...(htmlInputSlotProps ? { htmlInput: htmlInputSlotProps } : {}),
        }}
        className={`${changeRequested ? 'changesRequested' : ''} ${ignoreFs ? 'fs-exclude' : ''}`.trim() || undefined}
      />
    </InputLabelText>
  );
};

export default TextInput;
